import { useHistory } from 'react-router-dom';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { capitalize } from 'lodash';
import { router } from 'new/routes/router';
import { AppContext } from 'new/contexts/AppContext';
import { useDispatch } from 'react-redux';
import { hydrateState } from 'new/state/hashedState/action';
import useQueryParams from 'new/hooks/useQueryParams';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import { isAllowedEmbeddedParentOrigin } from 'js/constants/config';

type EmbeddedThemeMode = 'dark' | 'light';

function isEmbeddedThemeMode(value: unknown): value is EmbeddedThemeMode {
  return value === 'dark' || value === 'light';
}

export function useApp() {
  return useContext(AppContext);
}

function getBreadcrumbList(location) {
  if (location.pathname === '/') {
    return { slugs: ['Overview'], breadcrumbList: [''] };
  }
  const slugs = location.pathname.split('/').filter((slug) => slug !== '');
  const breadcrumbList = slugs.reduce(
    (acc, slug) => [...acc, acc[acc.length - 1] ? `${acc[acc.length - 1]}/${slug}` : `/${slug}`],
    [],
  );
  return { slugs, breadcrumbList };
}

export function AppContextProvider({ children }) {
  const history = useHistory();
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const { hashedState, embedded, mode: urlMode = 'light' } = useQueryParams();
  const [embeddedThemeMode, setEmbeddedThemeMode] = useState<EmbeddedThemeMode>(
    isEmbeddedThemeMode(urlMode) ? urlMode : 'light',
  );
  const [subheader, setSubHeader] = useState({
    slugs: [],
    breadcrumbList: [],
  });
  const [hideSideBar, setHide] = useState(false);
  const [headerTitle, setHeaderTitle] = useState('');
  const [currentSlug, setCurrentSlug] = useState('');
  const [headerRightView, setHeaderRightView] = useState<React.ReactNode>(null);

  const setHideSideBar = useCallback((hide) => {
    setHide(hide);
  }, []);

  function setupHeader(location) {
    const { slugs, breadcrumbList } = getBreadcrumbList(location);
    const defaultTitle = capitalize(slugs[breadcrumbList.length - 1]);
    const currentRoute = router.manager.getByPathname(location.pathname);

    // If route has manageBreadcrumbs flag set to true, skip URL-based generation
    // The component will manage its own breadcrumbs
    const hasCustomBreadcrumbs = currentRoute?.manageBreadcrumbs === true;

    if (!hasCustomBreadcrumbs) {
      // Normal flow: use URL-based breadcrumbs
      setSubHeader({ slugs, breadcrumbList });
    }

    // Handle header title and slug title
    if (currentRoute?.header) {
      if (currentRoute?.header?.title) {
        setHeaderTitle(currentRoute.header.title);
      } else {
        setHeaderTitle(defaultTitle);
      }
      if (currentRoute?.header?.slugTitle) {
        setCurrentSlug(currentRoute.header.slugTitle);
      } else if (!hasCustomBreadcrumbs) {
        setCurrentSlug(defaultTitle);
      }
    } else {
      setHeaderTitle(defaultTitle);
      setCurrentSlug(defaultTitle);
    }
  }

  useEffect(() => {
    if (hashedState) {
      dispatch(hydrateState(hashedState));
    }
  }, []);

  useEffect(() => {
    setupHeader(history.location);
    history.listen(setupHeader);
  }, []);

  const isEmbeddedMode = embedded === 'true';
  const mode: EmbeddedThemeMode = isEmbeddedMode
    ? embeddedThemeMode
    : isEmbeddedThemeMode(urlMode)
      ? urlMode
      : 'light';

  useEffect(() => {
    if (isEmbeddedThemeMode(urlMode)) {
      setEmbeddedThemeMode(urlMode);
    }
  }, [urlMode]);

  useEffect(() => {
    document.documentElement.classList.toggle('nexla-embedded', isEmbeddedMode);
    return () => {
      document.documentElement.classList.remove('nexla-embedded');
    };
  }, [isEmbeddedMode]);

  useEffect(() => {
    if (!isEmbeddedMode) {
      return;
    }

    const handleThemeMessage = (event: MessageEvent) => {
      if (!isAllowedEmbeddedParentOrigin(event.origin)) {
        return;
      }

      if (event.data?.type !== 'NEXLA_THEME_CHANGE') {
        return;
      }

      const nextMode = event.data.mode;
      if (isEmbeddedThemeMode(nextMode)) {
        setEmbeddedThemeMode(nextMode);
      }
    };

    window.addEventListener('message', handleThemeMessage);
    return () => {
      window.removeEventListener('message', handleThemeMessage);
    };
  }, [isEmbeddedMode]);

  const contextValue = useMemo(() => {
    const { slugs = [], breadcrumbList = [] } = subheader || {};
    const updatedSlugs = [...slugs];
    if (updatedSlugs.length > 0 && currentSlug) {
      updatedSlugs[updatedSlugs.length - 1] = currentSlug;
    }
    return {
      headerTitle,
      hideSideBar,
      setHeaderTitle,
      setHideSideBar,
      setSubHeader,
      breadcrumbList,
      slugs: updatedSlugs,
      currentSlug,
      setCurrentSlug,
      headerRightView,
      setHeaderRightView,
      isEmbeddedMode,
      mode,
    };
  }, [
    headerTitle,
    currentSlug,
    subheader,
    setHeaderTitle,
    hideSideBar,
    headerRightView,
    isEmbeddedMode,
    mode,
  ]);

  return (
    // @ts-ignore
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
}
